import React, { useState, useEffect } from 'react';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Button, Card, Typography, Box, Avatar, Collapse, Autocomplete, TextField, FormHelperText, Paper, Divider, Select, MenuItem, Tooltip
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import PersonIcon from '@mui/icons-material/Person';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { Platform } from '../types';

// Extend SocialAccount type locally to include oauthEmail for compatibility with backend
type SocialAccount = {
  platform: Platform;
  handle?: string;
  name?: string;
  oauthEmail?: string;
  [key: string]: any;
};
type DashboardPlatform = Exclude<Platform, 'google'>;
import { Chart, CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Tooltip as ChartTooltip, Legend, Filler } from 'chart.js';
Chart.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, ChartTooltip, Legend, Filler);

axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URL || 'https://social.zherolabs.com';
axios.interceptors.request.use(config => {
  const token = localStorage.getItem('jwtToken');
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

type AccountSelector = { handle: string; name?: string };
const platforms: Platform[] = [
  'facebook', 'instagram', 'youtube', 'twitter', 'linkedin', 'googleAnalytics', 'googleSearchConsole'
];
const platformIcons: Record<string, React.ReactNode> = {
  facebook: <FacebookIcon color="primary" />,
  instagram: <InstagramIcon color="secondary" />,
  youtube: <YouTubeIcon color="error" />,
  twitter: <TwitterIcon color="primary" />,
  linkedin: <LinkedInIcon color="primary" />,
  googleAnalytics: <AnalyticsIcon color="action" />,
  googleSearchConsole: <SearchIcon color="action" />
};
const platformFields: Record<string, Array<{ label: string; key: string; type?: string }>> = {
  Facebook: [ { label: 'Hashtags', key: 'hashtags' }, { label: 'Mentions', key: 'mentions' }, { label: 'Link', key: 'link' } ],
  Instagram: [ { label: 'Hashtags', key: 'hashtags' }, { label: 'Mentions', key: 'mentions' } ],
  LinkedIn: [ { label: 'Link', key: 'link' }, { label: 'Mentions', key: 'mentions' } ],
  Twitter: [ { label: 'Hashtags', key: 'hashtags' }, { label: 'Mentions', key: 'mentions' } ],
  YouTube: [ { label: 'Video Link', key: 'videoLink' }, { label: 'Tags', key: 'tags' } ],
};
const DRAFTS_KEY = 'socialMediaDrafts';
const TEMPLATES_KEY = 'socialMediaTemplates';
function saveDraft(draft: any) {
  const rawDrafts = localStorage.getItem(DRAFTS_KEY);
  const drafts = rawDrafts ? JSON.parse(rawDrafts) : [];
  drafts.push(draft);
  localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
}
function loadDrafts() {
  const raw = localStorage.getItem(DRAFTS_KEY);
  return raw ? JSON.parse(raw) : [];
}
function deleteDraft(index: number) {
  const drafts = loadDrafts(); drafts.splice(index, 1); localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
}
function saveTemplate(template: any) {
  const rawTemplates = localStorage.getItem(TEMPLATES_KEY);
  const templates = rawTemplates ? JSON.parse(rawTemplates) : [];
  templates.push(template);
  localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
}
function loadTemplates() {
  const raw = localStorage.getItem(TEMPLATES_KEY);
  return raw ? JSON.parse(raw) : [];
}
function deleteTemplate(index: number) {
  const templates = loadTemplates(); templates.splice(index, 1); localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
}
function logEvent(event: string, details?: any) {
  const log = { timestamp: new Date().toISOString(), event, details };
  console.log('[InputDashboard Log]', log);
}

function ProfileAccountsPanel({
  profile,
  connectedAccounts,
  handleConnect,
  setProfilePanelOpen
}: {
  profile: { name: string; email: string; avatar?: string },
  connectedAccounts: SocialAccount[],
  handleConnect: (platform: Platform) => void,
  setProfilePanelOpen: (open: boolean) => void,
}) {
  // Determine which platforms are already connected
  const connectedPlatformSet = new Set(connectedAccounts.map(acc => acc.platform));
  return (
    <Paper sx={{ maxWidth: 700, mx: 'auto', mt: 3, mb: 3, p: 4, borderRadius: 4, boxShadow: 8 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar src={profile.avatar} sx={{ width: 56, height: 56, mr: 2 }} />
        <Box>
          <Typography variant="h6">{profile.name}</Typography>
          <Typography variant="body2" color="text.secondary">{profile.email}</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" color="primary" onClick={() => setProfilePanelOpen(false)}>Close</Button>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Connected Accounts</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {platforms.map(platform => (
          <Box key={platform} sx={{ flex: '1 1 300px', minWidth: 260, maxWidth: 340, mb: 2 }}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {platformIcons[platform]}
                <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 500 }}>
                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => handleConnect(platform)}
                >
                  Connect
                </Button>
              </Box>
            </Card>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

const InputDashboard: React.FC = () => {
  // State
  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem('jwtToken'));
  const [profilePanelOpen, setProfilePanelOpen] = useState(false);
  const [analytics, setAnalytics] = useState<any>(null);
  // Auto-open profile panel if hash is #profile-section
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#profile-section' && !profilePanelOpen) {
      const timeout = setTimeout(() => setProfilePanelOpen(true), 100);
      return () => clearTimeout(timeout);
    }
  }, [profilePanelOpen]);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [dragActive, setDragActive] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [useSameForAll, setUseSameForAll] = useState(true);
  const [sharedCaption, setSharedCaption] = useState('');
  const [sharedHashtags, setSharedHashtags] = useState<string[]>([]);
  const [sharedScheduledAt, setSharedScheduledAt] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([]);
  const [connectedAccounts, setConnectedAccounts] = useState<SocialAccount[]>([]);
  const [platformStates, setPlatformStates] = useState<{
    [key in Platform]?: {
      caption: string;
      hashtags: string[];
      account: AccountSelector | null;
      scheduledAt: string;
      customFields: { [key: string]: string };
    }
  }>({});
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<any[]>(loadDrafts());
  const [templates, setTemplates] = useState<any[]>(loadTemplates());
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<{ name: string, email: string, avatar?: string }>({ name: 'User', email: 'user@email.com' });
  // Load profile from localStorage or backend
  useEffect(() => {
    const storedProfile = localStorage.getItem('profile');
    if (storedProfile) {
      try {
        setProfile(JSON.parse(storedProfile));
      } catch (e) {
        setProfile({ name: 'User', email: 'user@email.com' });
      }
    } else {
      const token = localStorage.getItem('jwtToken');
      if (token) {
  axios.get(`${axios.defaults.baseURL}/api/auth/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          setProfile(res.data.profile);
          localStorage.setItem('profile', JSON.stringify(res.data.profile));
        }).catch(() => {});
      }
    }
  }, []);

  // Handle Instagram/Twitter OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;
    if (pathname === '/instagram/callback') {
      if (params.get('success')) {
        setSuccessMsg('Instagram account connected successfully!');
        // Optionally, refresh connected accounts here
      } else if (params.get('error')) {
        setErrorMsg('Instagram connection failed: ' + params.get('error'));
      }
      window.history.replaceState({}, document.title, '/');
    }
    if (pathname === '/twitter/callback') {
      if (params.get('success')) {
        setSuccessMsg('Twitter account connected successfully!');
        // Optionally, refresh connected accounts here
      } else if (params.get('error')) {
        setErrorMsg('Twitter connection failed: ' + params.get('error'));
      }
      window.history.replaceState({}, document.title, '/');
    }
  }, []);

  // Effects
  useEffect(() => {
    // Fetch connected accounts on mount, only if logged in
    const fetchAccounts = async () => {
      const token = localStorage.getItem('jwtToken');
      if (!token) return;
      try {
        let accounts = [];
        try {
          const res = await axios.get('/api/accounts', { headers: { Authorization: `Bearer ${token}` } });
          accounts = res.data.accounts || [];
        } catch {
          const res = await axios.get('/api/accounts', { headers: { Authorization: `Bearer ${token}` } });
          if (Array.isArray(res.data.accounts)) {
            accounts = res.data.accounts;
          } else if (Array.isArray(res.data.platforms)) {
            accounts = res.data.platforms.map((p: string) => ({ platform: p, handle: p }));
          }
        }
        setConnectedAccounts(accounts);
      } catch (err) {
        // Type guard for error
        if (typeof err === 'object' && err !== null && 'response' in err && (err as any).response && (err as any).response.status === 401) {
          // Auto-logout on 401
          localStorage.removeItem('jwtToken');
          window.location.reload();
        } else {
          setErrorMsg('Failed to fetch connected accounts.');
        }
      }
    };
    const token = localStorage.getItem('jwtToken');
    if (token) {
      fetchAccounts();
    }
  }, []);

  // Handlers
  const handleConnect = async (platform: Platform) => {
    console.log('[handleConnect] called for platform:', platform);
    logEvent('connect_account', { platform });
    setStatusMsg(`Connecting to ${platform.charAt(0).toUpperCase() + platform.slice(1)}...`);
    try {
      const token = localStorage.getItem('jwtToken');
      // Fetch OAuth URL from backend (mode=url returns JSON)
      const res = await fetch(`https://social.zherolabs.com/api/oauth?provider=${platform}&mode=url`, {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) {
        setErrorMsg('Failed to get OAuth URL from backend.');
        return;
      }
      const data = await res.json();
      const oauthUrl = data.url;
      if (!oauthUrl) {
        setErrorMsg('Failed to get OAuth URL from backend.');
        return;
      }
      if (platform === 'twitter') {
        // Twitter: open in popup
        const authWindow = window.open('about:blank', '_blank', 'width=500,height=700');
        if (!authWindow) {
          setErrorMsg('Popup was blocked by your browser. Please allow popups for this site.');
          return;
        } else {
          setTimeout(() => {
            try {
              console.log('[handleConnect] Redirecting popup to OAuth URL:', oauthUrl);
              authWindow.location.href = oauthUrl;
            } catch (e) {
              console.log('[handleConnect] Failed to set popup location:', e);
            }
          }, 100);
          console.log('[handleConnect] Popup opened successfully');
        }
        // Poll for connection (simple example, production should use postMessage or backend callback)
        const pollInterval = setInterval(async () => {
          if (authWindow && authWindow.closed) {
            clearInterval(pollInterval);
            console.log('[handleConnect] Popup closed, refreshing connected accounts');
            // After OAuth, refresh connected accounts
            try {
              let accounts = [];
              try {
                const res = await axios.get('/api/accounts');
                accounts = res.data.accounts || [];
              } catch {
                const res = await axios.get('/api/accounts');
                if (Array.isArray(res.data.accounts)) {
                  accounts = res.data.accounts;
                } else if (Array.isArray(res.data.platforms)) {
                  accounts = res.data.platforms.map((p: string) => ({ platform: p, handle: p }));
                }
              }
              setConnectedAccounts(accounts);
              setStatusMsg(`${platform.charAt(0).toUpperCase() + platform.slice(1)} account connected!`);
            } catch (err) {
              setErrorMsg('Failed to refresh connected accounts.');
            }
          }
        }, 1000);
      } else {
  // All other providers: redirect in same tab
  console.log('[handleConnect] Redirecting browser to OAuth URL:', oauthUrl);
  window.location.href = oauthUrl;
      }
    } catch (err) {
      console.log('[handleConnect] Error:', err);
      setErrorMsg('Failed to initiate connection.');
    }
  };

  const handleMediaUpload = (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
    let files: File[] = [];
    if ('dataTransfer' in e) {
      files = Array.from(e.dataTransfer.files);
    } else if (e.target && 'files' in e.target && e.target.files) {
      files = Array.from(e.target.files);
    }
    setMediaFiles(files);
    const urls = files.map(file => URL.createObjectURL(file));
    setMediaUrls(urls);
    logEvent('media_upload', { count: files.length });
  };

  // Validation
  const validatePost = (): string | null => {
    if (selectedPlatforms.length === 0) return 'Please select at least one platform.';
    if (useSameForAll) {
      if (!sharedCaption.trim()) return 'Caption is required.';
      if (!sharedScheduledAt) return 'Schedule is required.';
      for (const platform of selectedPlatforms) {
        // You may want to check for handle/account selection here if needed
      }
    } else {
      for (const platform of selectedPlatforms) {
        const state = platformStates[platform];
        if (!state?.caption?.trim()) return `Caption required for ${platform}.`;
        if (!state?.scheduledAt) return `Schedule required for ${platform}.`;
        if (!state?.account?.handle) return `Select an account for ${platform}.`;
      }
    }
    return null;
  };

  // Post Handler
  const handlePost = async () => {
    setErrorMsg(null);
    setStatusMsg(null);
    const validationError = validatePost();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }
    setLoading(true);
    try {
      // Build handles object: { platform: [{ handle, name }] }
      const handles: Record<string, AccountSelector[]> = {};
      selectedPlatforms.forEach(platform => {
        let account = useSameForAll
          ? { handle: connectedAccounts.find(acc => acc.platform === platform)?.handle || '' }
          : platformStates[platform]?.account;
        if (account && account.handle) handles[platform] = [account];
      });

      // Upload media files first if any
      let uploadedMediaUrls: string[] = [];
      if (mediaFiles.length > 0) {
        const formData = new FormData();
        mediaFiles.forEach(file => formData.append('media', file));
        const uploadRes = await axios.post('/api/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedMediaUrls = (uploadRes.data.files || []).map((f: any) => f.url);
      }

      const payload = {
        caption: sharedCaption,
        platforms: selectedPlatforms,
        handles,
        hashtags: sharedHashtags,
        scheduledAt: sharedScheduledAt,
        mediaUrls: uploadedMediaUrls,
        mediaFiles: mediaFiles.length,
        // Optionally include platformStates, useSameForAll, etc. if backend uses them
      };
      logEvent('post_submit', payload);
      await axios.post('/api/posts', payload);
      setStatusMsg('Post submitted successfully!');
      setLoading(false);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err?.response?.data?.message || 'Failed to submit post.');
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <span id="profile-section"></span>
      {profilePanelOpen && (
        <ProfileAccountsPanel
          profile={profile}
          connectedAccounts={connectedAccounts}
          handleConnect={handleConnect}
          setProfilePanelOpen={setProfilePanelOpen}
        />
      )}
      <Box sx={{ maxWidth: 700, mx: 'auto', mt: 2, mb: 2, textAlign: 'right' }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<PersonIcon />}
          onClick={() => setProfilePanelOpen(true)}
          id="open-profile-btn"
        >
          Profile & Accounts
        </Button>
      </Box>
  {/* Auto-open profile panel if hash is #profile-section (handled in useEffect) */}
      <Paper sx={{ maxWidth: 700, mx: 'auto', p: 4, borderRadius: 4, boxShadow: 6 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, textAlign: 'center', color: '#3182ce' }}>
          Create Social Media Post
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {/* Shared Caption/Hashtags Section */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">Caption</Typography>
            <Tooltip title="Write your post caption. Use hashtags and links as needed." arrow>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={sharedCaption}
                onChange={e => setSharedCaption(e.target.value)}
                placeholder="Write your caption here..."
                sx={{ mt: 1 }}
              />
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">Hashtags</Typography>
            <Tooltip title="Comma separated hashtags. Auto-detected from caption." arrow>
              <TextField
                fullWidth
                value={sharedHashtags.join(', ')}
                onChange={e => setSharedHashtags(e.target.value.split(',').map(h => h.trim()).filter(Boolean))}
                placeholder="#marketing, #automation"
                sx={{ mt: 1 }}
              />
            </Tooltip>
          </Box>
          <Box sx={{ minWidth: 180 }}>
            <Typography variant="h6">Schedule</Typography>
            <TextField
              type="datetime-local"
              value={sharedScheduledAt}
              onChange={e => setSharedScheduledAt(e.target.value)}
              sx={{ mt: 1, minWidth: 160 }}
            />
          </Box>
        </Box>
        <Box sx={{ mb: 2 }}>
          <FormHelperText>
            Use the same caption, hashtags, and schedule for all platforms?
          </FormHelperText>
          <Button
            variant={useSameForAll ? 'contained' : 'outlined'}
            color="primary"
            sx={{ mr: 2 }}
            onClick={() => setUseSameForAll(true)}
          >
            Same for All
          </Button>
          <Button
            variant={!useSameForAll ? 'contained' : 'outlined'}
            color="secondary"
            onClick={() => setUseSameForAll(false)}
          >
            Customize Per Platform
          </Button>
        </Box>
        {/* Media Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6">Media</Typography>
          <Tooltip title="Upload images or videos. Drag & drop supported." arrow>
            <Box
              sx={{
                border: dragActive ? '2px dashed #3182ce' : '2px solid #e3e8f0',
                borderRadius: 3,
                p: 2,
                background: dragActive ? '#e3eafc' : '#f7faff',
                transition: 'background 0.2s, border 0.2s',
                cursor: 'pointer',
              }}
              onDragOver={e => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={e => { e.preventDefault(); setDragActive(false); }}
              onDrop={e => { e.preventDefault(); setDragActive(false); handleMediaUpload(e); }}
            >
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleMediaUpload}
                style={{ display: 'none' }}
                id="media-upload"
              />
              <label htmlFor="media-upload">
                <Button variant="outlined" color="primary" component="span" sx={{ mt: 1 }}>
                  Upload Media
                </Button>
              </label>
              <Typography variant="body2" sx={{ mt: 1, color: '#888' }}>
                or drag & drop files here
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                {mediaUrls.map((url: string) => (
                  <img key={url} src={url} alt="media preview" style={{ maxWidth: 60, borderRadius: 6, boxShadow: dragActive ? '0 0 8px #3182ce' : 'none', transition: 'box-shadow 0.2s' }} />
                ))}
              </Box>
            </Box>
          </Tooltip>
        </Box>
        {/* Platforms Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6">Platforms</Typography>
          <Autocomplete
            multiple
            options={platforms}
            value={selectedPlatforms}
            onChange={(_, value) => setSelectedPlatforms(value)}
            renderInput={params => (
              <TextField {...params} label="Select Platforms" />
            )}
            sx={{ mt: 1 }}
          />
        </Box>
        {/* Platform Windows */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          {selectedPlatforms.map((platform: Platform) => (
            <Card key={platform} sx={{ minWidth: 320, flex: '1 1 320px', background: 'linear-gradient(135deg, #f5f8fa 0%, #e9eafc 100%)', boxShadow: 6, borderRadius: 4, p: 2, transition: 'box-shadow 0.3s', '&:hover': { boxShadow: 12 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {platformIcons[platform]}
                <Typography variant="h6" sx={{ ml: 1 }}>{platform.charAt(0).toUpperCase() + platform.slice(1)}</Typography>
              </Box>
              <FormHelperText>Select Account</FormHelperText>
              <Select
                value={
                  platformStates[platform]?.account?.handle
                    ? `${platformStates[platform]?.account?.handle}|||${platformStates[platform]?.account?.name || ''}`
                    : ''
                }
                onChange={e => {
                  const [handle, name] = e.target.value.split('|||');
                  setPlatformStates(prev => ({ ...prev, [platform]: { ...prev[platform], account: { handle, name } } }));
                }}
                sx={{ mb: 2, minWidth: 180, transition: 'border-color 0.2s' }}
              >
                {connectedAccounts.filter((acc: SocialAccount) => acc.platform === platform).map((acc: SocialAccount) => {
                  // Choose display label: name > handle > oauthEmail
                  const displayLabel = acc.name?.trim() ? acc.name : (acc.handle?.trim() ? acc.handle : (acc.oauthEmail || ''));
                  return (
                    <MenuItem key={(acc.handle || acc.oauthEmail || '') + '|||' + (acc.name || '')} value={(acc.handle || acc.oauthEmail || '') + '|||' + (acc.name || '')}>
                      {displayLabel}
                    </MenuItem>
                  );
                })}
              </Select>
              {!useSameForAll && (
                <>
                  <TextField
                    label="Caption"
                    multiline
                    rows={2}
                    value={platformStates[platform]?.caption || ''}
                    onChange={e => setPlatformStates(prev => ({ ...prev, [platform]: { ...prev[platform], caption: e.target.value } }))}
                    fullWidth
                    sx={{ mb: 2, transition: 'border-color 0.2s' }}
                  />
                  <TextField
                    label="Hashtags"
                    value={platformStates[platform]?.hashtags?.join(', ') || ''}
                    onChange={e => setPlatformStates(prev => ({ ...prev, [platform]: { ...prev[platform], hashtags: e.target.value.split(',').map((h: string) => h.trim()).filter(Boolean) } }))}
                    fullWidth
                    sx={{ mb: 2, transition: 'border-color 0.2s' }}
                  />
                  <TextField
                    label="Schedule"
                    type="datetime-local"
                    value={platformStates[platform]?.scheduledAt || ''}
                    onChange={e => setPlatformStates(prev => ({ ...prev, [platform]: { ...prev[platform], scheduledAt: e.target.value } }))}
                    fullWidth
                    sx={{ mb: 2, transition: 'border-color 0.2s' }}
                  />
                  {platformFields[platform.charAt(0).toUpperCase() + platform.slice(1)]?.map(field => (
                    <TextField
                      key={field.key}
                      label={field.label}
                      value={platformStates[platform]?.customFields?.[field.key] || ''}
                      onChange={e => setPlatformStates(prev => ({
                        ...prev,
                        [platform]: {
                          ...prev[platform],
                          customFields: { ...prev[platform]?.customFields, [field.key]: e.target.value }
                        }
                      }))}
                      fullWidth
                      sx={{ mb: 2, transition: 'border-color 0.2s' }}
                    />
                  ))}
                </>
              )}
              {useSameForAll && (
                <FormHelperText>Using shared caption, hashtags, and schedule for all platforms.</FormHelperText>
              )}
              <Collapse in={true}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1">Preview:</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {(useSameForAll ? sharedCaption : platformStates[platform]?.caption) || ''}
                  </Typography>
                  {mediaUrls.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      {mediaUrls.map((url: string) => (
                        <img key={url} src={url} alt="media preview" style={{ maxWidth: 40, borderRadius: 4 }} />
                      ))}
                    </Box>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Hashtags: {(useSameForAll ? sharedHashtags : platformStates[platform]?.hashtags || []).join(', ')}
                  </Typography>
                </Box>
              </Collapse>
            </Card>
          ))}
        </Box>
  {statusMsg && <FormHelperText sx={{ mt: 2, color: 'green' }}>{statusMsg}</FormHelperText>}
  {errorMsg && <FormHelperText error sx={{ mt: 2 }}>{errorMsg}</FormHelperText>}
  {successMsg && <FormHelperText sx={{ mt: 2, color: 'green' }}>{successMsg}</FormHelperText>}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button variant="contained" color="primary" size="large" onClick={handlePost} disabled={loading}>
            {loading ? 'Posting...' : 'Post'}
          </Button>
        </Box>
      </Paper>
    </React.Fragment>
  );
}

export default InputDashboard;
